"use client"

import {
  addMonths,
  addYears,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
} from "date-fns"
import type React from "react"
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react"

export type TSelectionMode = "single" | "range" | "multiple"

export interface IDateRange {
  from: Date
  to: Date
}

export interface ICalendarContextType {
  // Current view state
  currentDate: Date
  setCurrentDate: (date: Date) => void

  // Selection mode and values
  mode: TSelectionMode
  setMode: (mode: TSelectionMode) => void
  selectedDate: Date | null
  selectedRange: IDateRange | null
  selectedDates: Date[]

  // Selection methods
  selectDate: (date: Date) => void
  selectRange: (date: Date) => void
  selectMultiple: (date: Date) => void

  // Navigation methods
  nextMonth: () => void
  prevMonth: () => void
  nextYear: () => void
  prevYear: () => void
  goToMonth: (month: number, year: number) => void

  // Utility methods
  clearSelection: () => void
  isDateSelected: (date: Date) => boolean
  isDateInRange: (date: Date) => boolean
  isStartOfRange: (date: Date) => boolean
  isEndOfRange: (date: Date) => boolean

  // Calendar data
  calendarDays: Date[]
  weekdays: string[]

  // Year selection
  isYearView: boolean
  setIsYearView: (isYearView: boolean) => void
  yearRange: number[]
  setYearRangeStart: (year: number) => void
}

const CalendarContext = createContext<ICalendarContextType | undefined>(
  undefined,
)

interface ICalendarProviderProps {
  children: React.ReactNode
  defaultMode?: TSelectionMode
  defaultDate?: Date
  defaultSelectedDate?: Date
  defaultSelectedRange?: IDateRange
  defaultSelectedDates?: Date[]
  yearRangeCount?: number
  onSelect?: (date: Date | { from: Date; to: Date } | Date[] | null) => void
}

export const CalendarProvider: React.FC<ICalendarProviderProps> = ({
  children,
  defaultMode = "single",
  defaultDate = new Date(),
  defaultSelectedDate = null,
  defaultSelectedRange = null,
  defaultSelectedDates = [],
  yearRangeCount = 12,
  onSelect,
}) => {
  // View state
  const [currentDate, setCurrentDate] = useState<Date>(defaultDate)
  const [isYearView, setIsYearView] = useState<boolean>(false)
  const [yearRangeStart, setYearRangeStart] = useState<number>(
    defaultDate.getFullYear() - Math.floor(yearRangeCount / 2),
  )

  // Selection state
  const [mode, setMode] = useState<TSelectionMode>(defaultMode)
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultSelectedDate,
  )
  const [selectedRange, setSelectedRange] = useState<IDateRange | null>(
    defaultSelectedRange,
  )
  const [selectedDates, setSelectedDates] = useState<Date[]>(
    defaultSelectedDates || [],
  )
  const [rangeSelectionStart, setRangeSelectionStart] = useState<Date | null>(
    null,
  )

  // Generate calendar days for current month view
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(currentDate)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    return eachDayOfInterval({ start: startDate, end: endDate })
  }, [currentDate])

  // Generate weekday headers
  const weekdays = useMemo(() => {
    const weekStart = startOfWeek(new Date())
    return Array.from({ length: 7 }).map((_, i) => {
      const day = addDays(weekStart, i)
      return format(day, "EEEEEE") // 2-letter abbreviation
    })
  }, [])

  // Generate year range for year view
  const yearRange = useMemo(() => {
    return Array.from({ length: yearRangeCount }).map(
      (_, i) => yearRangeStart + i,
    )
  }, [yearRangeStart, yearRangeCount])

  // Navigation methods
  const nextMonth = useCallback(() => {
    setCurrentDate((prev) => addMonths(prev, 1))
  }, [])

  const prevMonth = useCallback(() => {
    setCurrentDate((prev) => addMonths(prev, -1))
  }, [])

  const nextYear = useCallback(() => {
    setCurrentDate((prev) => addYears(prev, 1))
  }, [])

  const prevYear = useCallback(() => {
    setCurrentDate((prev) => addYears(prev, -1))
  }, [])

  const goToMonth = useCallback(
    (month: number, year: number) => {
      const newDate = new Date(currentDate)
      newDate.setMonth(month)
      newDate.setFullYear(year)
      setCurrentDate(newDate)
      setIsYearView(false)
    },
    [currentDate],
  )

  // Selection methods
  const selectDate = useCallback(
    (date: Date) => {
      setSelectedDate(date)
      setCurrentDate(date)
      onSelect?.(date)
    },
    [onSelect],
  )

  const selectRange = useCallback(
    (date: Date) => {
      if (!rangeSelectionStart) {
        setRangeSelectionStart(date)
        setSelectedRange({ from: date, to: date })
        onSelect?.({ from: date, to: date })
      } else {
        // Ensure from date is earlier than to date
        const from = rangeSelectionStart < date ? rangeSelectionStart : date
        const to = rangeSelectionStart < date ? date : rangeSelectionStart

        setSelectedRange({ from, to })
        onSelect?.({ from, to })
        setRangeSelectionStart(null)
      }
    },
    [rangeSelectionStart, onSelect],
  )

  const selectMultiple = useCallback(
    (date: Date) => {
      setSelectedDates((prev) => {
        // If date is already selected, remove it
        const isAlreadySelected = prev.some((d) => isSameDay(d, date))

        if (isAlreadySelected) {
          return prev.filter((d) => !isSameDay(d, date))
        }
        return [...prev, date]
      })

      onSelect?.(selectedDates)
    },
    [onSelect, selectedDates],
  )

  // Utility methods
  const clearSelection = useCallback(() => {
    setSelectedDate(null)
    setSelectedRange(null)
    setSelectedDates([])
    setRangeSelectionStart(null)
    onSelect?.(null)
  }, [onSelect])

  const isDateSelected = useCallback(
    (date: Date) => {
      if (mode === "single") {
        return selectedDate !== null && isSameDay(date, selectedDate)
      }
      if (mode === "multiple") {
        return selectedDates.some((d) => isSameDay(d, date))
      }
      return false
    },
    [mode, selectedDate, selectedDates],
  )

  const isDateInRange = useCallback(
    (date: Date) => {
      if (mode !== "range" || !selectedRange) return false

      return isWithinInterval(date, {
        start: selectedRange.from,
        end: selectedRange.to,
      })
    },
    [mode, selectedRange],
  )

  const isStartOfRange = useCallback(
    (date: Date) => {
      if (mode !== "range" || !selectedRange) return false
      return isSameDay(date, selectedRange.from)
    },
    [mode, selectedRange],
  )

  const isEndOfRange = useCallback(
    (date: Date) => {
      if (mode !== "range" || !selectedRange) return false
      return isSameDay(date, selectedRange.to)
    },
    [mode, selectedRange],
  )

  const value = {
    currentDate,
    setCurrentDate,
    mode,
    setMode,
    selectedDate,
    selectedRange,
    selectedDates,
    selectDate,
    selectRange,
    selectMultiple,
    nextMonth,
    prevMonth,
    nextYear,
    prevYear,
    goToMonth,
    clearSelection,
    isDateSelected,
    isDateInRange,
    isStartOfRange,
    isEndOfRange,
    calendarDays,
    weekdays,
    isYearView,
    setIsYearView,
    yearRange,
    setYearRangeStart,
  }

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  )
}

export const useCalendar = () => {
  const context = useContext(CalendarContext)
  if (context === undefined) {
    throw new Error("useCalendar must be used within a CalendarProvider")
  }
  return context
}

// Helper function to add days
function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(date.getDate() + days)
  return result
}
